import React, { useState } from "react";
import {
  Box,
  Grid,
  Typography,
  TextField,
  MenuItem,
  Button,
  Card,
  CardContent,
  Snackbar,
  Alert,
} from "@mui/material";
import { motion } from "framer-motion";
import "../styles/CostEstimator.css";

const API_BASE = "https://my-infraservice-7.onrender.com";

const CostEstimator = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    plotSize: "",
    floors: "",
    location: "Bengaluru",
  });

  const [builtUpArea, setBuiltUpArea] = useState(0);
  const [showCost, setShowCost] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const packages = [
    { name: "Basic Package", rate: 1400, color: "#E3F2FD" },
    { name: "Standard Package", rate: 1600, color: "#BBDEFB" },
    { name: "Premium Package", rate: 1800, color: "#90CAF9" },
    { name: "Luxury Package", rate: 2100, color: "#64B5F6" },
    { name: "Labour Contract", rate: 350, color: "#01b41fff", textColor: "#fff" },
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const calculateBuiltUpArea = (size, floors) => {
    try {
      const [len, wid] = size.split("*").map(Number);
      if (!len || !wid || !floors) return 0;
      return len * wid * parseInt(floors);
    } catch {
      return 0;
    }
  };

  const validateForm = () => {
    const phoneRegex = /^[6-9]\d{9}$/;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    if (!form.name.trim()) {
      setSnackbar({ open: true, message: "Name is required!", severity: "error" });
      return false;
    }

    if (!form.phone.trim()) {
      setSnackbar({ open: true, message: "Phone number is required!", severity: "error" });
      return false;
    }

    if (!phoneRegex.test(form.phone)) {
      setSnackbar({
        open: true,
        message: "Enter a valid 10-digit phone number!",
        severity: "error",
      });
      return false;
    }

    if (form.email && !emailRegex.test(form.email)) {
      setSnackbar({
        open: true,
        message: "Enter a valid email address!",
        severity: "error",
      });
      return false;
    }

    return true;
  };

  const handleEstimate = async () => {
    if (!validateForm()) return;

    const area = calculateBuiltUpArea(form.plotSize, form.floors);
    setBuiltUpArea(area);
    setShowCost(true);

    try {
      await fetch(`${API_BASE}/api/save_estimate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ...form, builtUpArea: area }),
      });
      setSnackbar({
        open: true,
        message: "Estimate saved successfully!",
        severity: "success",
      });
    } catch (err) {
      setSnackbar({ open: true, message: "Error saving data!", severity: "error" });
    }
  };

  return (
    <Box className="estimate-wrapper">
      <motion.div
        initial={{ opacity: 0, y: -30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Typography variant="h4" className="estimate-title">
          🏗️ Cost Estimator
        </Typography>
      </motion.div>

      <Grid container spacing={3} className="estimate-form">
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Name *"
            name="name"
            value={form.name}
            onChange={handleChange}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Phone Number *"
            name="phone"
            value={form.phone}
            onChange={handleChange}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Email (optional)"
            name="email"
            value={form.email}
            onChange={handleChange}
            variant="outlined"
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Plot Size (e.g. 30*40)"
            name="plotSize"
            value={form.plotSize}
            onChange={handleChange}
            variant="outlined"
          />
        </Grid>
        {/* <Grid item xs={6} sm={4}> */}
          <TextField
            select
            fullWidth
            label="No. of Floors"
            name="floors"
            value={form.floors}
            onChange={handleChange}
            variant="outlined"
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  style: {
                    zIndex: 2000,
                    backgroundColor: "#fff",
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.2)",
                    borderRadius: "10px",
                  },
                },
              },
            }}
          >
            {["G+1", "G+2", "G+3", "G+4", "G+5"].map((f) => (
              <MenuItem key={f} value={f.replace("G+", "")}>
                {f}
              </MenuItem>
            ))}
          </TextField>
        {/* </Grid> */}

        <Grid item xs={12} sm={6}>
          <TextField
            select
            fullWidth
            label="Location"
            name="location"
            value={form.location}
            onChange={handleChange}
            variant="outlined"
          >
            <MenuItem value="Bengaluru">Bengaluru</MenuItem>
            <MenuItem value="Mysuru">Mysuru</MenuItem>
            <MenuItem value="Hyderabad">Hyderabad</MenuItem>
          </TextField>
        </Grid>
      </Grid>

      <Box textAlign="center" mt={4}>
        <motion.div whileHover={{ scale: 1.05 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleEstimate}
            className="estimate-btn"
          >
            Estimate Costing
          </Button>
        </motion.div>
      </Box>

      {showCost && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.4 }}>
          <Grid container spacing={3} mt={4} justifyContent="center">
            {packages.map((pkg, i) => (
              <Grid item xs={12} sm={6} md={4} key={i}>
                <motion.div whileHover={{ scale: 1.05 }}>
                  <Card
                    sx={{
                      backgroundColor: pkg.color,
                      color: pkg.textColor || "#000",
                      borderRadius: 3,
                      boxShadow: "0 6px 12px rgba(0,0,0,0.15)",
                    }}
                  >
                    <CardContent>
                      <Typography variant="h6" align="center" fontWeight={700}>
                        {pkg.name}
                      </Typography>
                      <Typography align="center">
                        Plot: {form.plotSize} | Floors: {form.floors}
                      </Typography>
                      <Typography align="center">
                        Built-up Area: {builtUpArea} sq.ft
                      </Typography>

                      {/* ✅ Architectural Design Info */}
                      <Typography
                        align="center"
                        sx={{ fontStyle: "italic", color: "#4c413eff", mt: 1 }}
                      >
                        Architectural Designs Included 🏠
                      </Typography>

                      <Typography
                        variant="h6"
                        align="center"
                        fontWeight={600}
                        mt={1}
                      >
                        ₹{(builtUpArea * pkg.rate).toLocaleString()}
                      </Typography>
                    </CardContent>
                  </Card>
                </motion.div>
              </Grid>
            ))}
          </Grid>
        </motion.div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CostEstimator;


